import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getLocationsByRegion } from '../../lib/sanity';
import { useNav } from '../../context/NavContext';
import { sortLocationsByDefault } from '../../utils/locationSorting';

/**
 * LocationsDropdown component for displaying a mega menu of all locations
 * 
 * @returns {JSX.Element} LocationsDropdown component
 */
export default function LocationsDropdown() {
  const [locationsByRegion, setLocationsByRegion] = useState({});
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);
  const { isDropdownActive, toggleDropdown, closeDropdown } = useNav();
  
  const isOpen = isDropdownActive('locations');

  // Fetch locations grouped by region
  useEffect(() => {
    async function fetchLocations() {
      try {
        setLoading(true);
        const locations = await getLocationsByRegion();
        setLocationsByRegion(locations);
      } catch (error) {
        console.error('Error fetching locations:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchLocations();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown('locations');
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeDropdown]);

  // Format region name for display
  const formatRegionName = (region) => {
    if (region === 'washington') return 'Washington State';
    if (region === 'new-york') return 'New York';
    
    return region
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get sorted region names
  const regionNames = Object.keys(locationsByRegion).sort((a, b) => {
    const order = { 'washington': 1, 'indiana': 2, 'new-york': 3 };
    const orderA = order[a] || 99;
    const orderB = order[b] || 99;
    
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return a.localeCompare(b);
  });
  
  // Organize regions for 2-column layout
  const leftColumnRegions = regionNames.filter(region => region === 'washington');
  const rightColumnRegions = regionNames.filter(region => region === 'indiana' || region === 'new-york');

  // Custom sort order for Washington locations
  const washingtonLocationOrder = [
    'bothell',
    'redmond',
    'maple-valley',
    'gig-harbor',
    'tacoma',
    'puyallup',
    'richland',
    'kennewick'
  ];

  // Sort locations within each region
  const getSortedLocations = (region, locations) => {
    if (!locations) return [];
    
    if (region === 'washington') {
      // Sort Washington locations using the custom order
      return [...locations].sort((a, b) => {
        // Extract city from slug (e.g., "bothell-wa" -> "bothell")
        const cityA = a.slug?.split('-').slice(0, -1).join('-') || a.slug;
        const cityB = b.slug?.split('-').slice(0, -1).join('-') || b.slug;
        
        const indexA = washingtonLocationOrder.indexOf(cityA);
        const indexB = washingtonLocationOrder.indexOf(cityB);
        
        // If both are in the order list, sort by order
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }
        // If only one is in the list, prioritize it
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        // Otherwise, sort alphabetically by name
        return a.name.localeCompare(b.name);
      });
    }
    
    // For other regions, sort alphabetically by name
    return [...locations].sort((a, b) => a.name.localeCompare(b.name));
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Trigger */}
      <button
        className="text-brand-navy hover:text-brand-orange transition-colors flex items-center"
        onClick={() => toggleDropdown('locations')}
        aria-expanded={isOpen}
        aria-haspopup="true"
        onMouseEnter={() => toggleDropdown('locations')}
      >
        <span>LOCATIONS</span>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          className={`h-4 w-4 ml-1 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Mega Menu Dropdown */}
      {isOpen && (
        <div 
          className="absolute left-0 mt-6 bg-brand-cream border border-brand-navy rounded-lg shadow-xl z-50 w-[700px] max-w-[95vw]"
          onMouseLeave={() => closeDropdown('locations')}
        >
          {/* Top banner */}
          <div className="bg-brand-navy text-white py-4 px-6 rounded-t-lg flex justify-between items-center">
            <h3 className="text-xl font-bold">OUR LOCATIONS</h3>
            <Link 
              to="/locations" 
              className="text-white hover:text-brand-yellow flex items-center text-sm bg-brand-navy bg-opacity-50 hover:bg-opacity-70 px-4 py-2 rounded-full transition-all"
              onClick={() => closeDropdown('locations')}
            >
              View All Locations
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </Link>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-navy"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left Column - Washington */}
                <div>
                  {leftColumnRegions.map(region => (
                    <div key={region} className="mb-4">
                      <div className="flex items-center mb-3">
                        <div className="w-6 h-6 bg-brand-navy rounded-full flex items-center justify-center mr-2">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-bold text-brand-navy">{formatRegionName(region)}</h4>
                      </div>
                      <div className="border-l-2 border-brand-navy pl-4 ml-3">
                        <ul className="space-y-3">
                          {getSortedLocations(region, locationsByRegion[region]).map(location => (
                            <li key={location._id}>
                              <Link
                                to={`/locations/${location.slug}`}
                                className="text-gray-700 hover:text-brand-orange transition-colors flex items-center group"
                                onClick={() => closeDropdown('locations')}
                              >
                                <span className="w-2 h-2 bg-brand-navy rounded-full mr-2 group-hover:bg-brand-orange transition-colors"></span>
                                <span>{location.name}</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Right Column - Indiana and New York */}
                <div>
                  {rightColumnRegions.map(region => (
                    <div key={region} className="mb-8">
                      <div className="flex items-center mb-3">
                        <div className="w-6 h-6 bg-brand-navy rounded-full flex items-center justify-center mr-2">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-bold text-brand-navy">{formatRegionName(region)}</h4>
                      </div>
                      <div className="border-l-2 border-brand-navy pl-4 ml-3">
                        <ul className="space-y-3">
                          {getSortedLocations(region, locationsByRegion[region]).map(location => (
                            <li key={location._id}>
                              <Link
                                to={`/locations/${location.slug}`}
                                className="text-gray-700 hover:text-brand-orange transition-colors flex items-center group"
                                onClick={() => closeDropdown('locations')}
                              >
                                <span className="w-2 h-2 bg-brand-navy rounded-full mr-2 group-hover:bg-brand-orange transition-colors"></span>
                                <span>{location.name}</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}